import { TransactionRowComponent } from '@varo/graphql-schema-types';
import { NativeStackHeaderProps, Stack, useRouter } from 'expo-router';
import { useFocusEffect } from 'expo-router/react-navigation';
import ActionViewHeader from 'features/lineOfCredit/screens/activeDetailsScreen/actionViewHeader/ActionViewHeader';
import LineOfCreditErrorScreenComponent, {
  VlocStepName,
} from 'features/lineOfCredit/screens/error/LineOfCreditErrorScreenComponent';
import React, { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import TransactionsSectionList from 'shared/components/TransactionSectionList/TransactionSectionList';
import IconButton from 'shared/components/vxl/icon/IconButton/IconButton';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';

import useVaroLineOfCreditGetDetails from './useVaroLineOfCreditGetDetails';

export interface LineOfCreditTransactionInput {
  transaction: TransactionRowComponent;
}

const ActiveDetailsScreen = () => {
  const router = useRouter();
  const {
    resultsObj: {
      amountDue,
      isAutoPayEnabled,
      paymentSchedule,
      remainingBalance,
      bannerMessage,
      dueDate,
      installmentsData,
      transactionsMap,
    },
    error,
    fetching,
    refetch,
  } = useVaroLineOfCreditGetDetails();

  const { bottom } = useSafeAreaInsets();

  const hasFocusedBefore = useRef(false);

  useFocusEffect(
    useCallback(() => {
      if (hasFocusedBefore.current) {
        refetch();
      } else {
        hasFocusedBefore.current = true;
      }
    }, [refetch]),
  );

  const { t: translatedText } = useTranslation('vloc', {
    keyPrefix: 'activeScreen',
  });

  useViewDidFocusScreenAnalyticsEvent({
    key: '/varo-line-of-credit/active-details',
  });

  return (
    <View style={[styles.container, { paddingBottom: bottom }]}>
      <Stack.Screen
        options={{
          title: translatedText('title'),
          headerBackVisible: !error,
          headerShown: !error,
          gestureEnabled: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar
              trailingAccessories={
                <IconButton
                  iconKey="SETTINGS"
                  iconSize="MD"
                  iconButtonAccessibilityLabel="SETTINGS"
                  iconOnPress={() => {
                    router.navigate({
                      pathname: '/varo-line-of-credit/manage-account',
                      params: { isActiveAccount: 'true' },
                    });
                  }}
                  analyticsId="active_details_manageAccountClicked"
                />
              }
              {...props}
            />
          ),
        }}
      />
      <ScreenTransitionOverlay
        isLoading={fetching}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={
          error ? (
            <LineOfCreditErrorScreenComponent
              screenAnalyticsId="/varo-line-of-credit/active-details-screen/errorScreenLoad"
              trackingAnalyticsId="active-details-screen-error-tracking"
              buttonPressKey="active-screen-errorbuttonpress"
              trackingCustomAttributes={{
                step_name: VlocStepName.VLOC_HOME_PAGE,
              }}
            />
          ) : null
        }
      />

      {transactionsMap && transactionsMap?.length > 0 ? (
        <TransactionsSectionList
          transactions={transactionsMap || []}
          header={{
            id: 'header',
            render: () => (
              <ActionViewHeader
                bannerMessage={bannerMessage || null}
                isAutoPayEnabled={isAutoPayEnabled || null}
                paymentSchedule={paymentSchedule || null}
                dueDate={dueDate || null}
                remainingBalance={remainingBalance}
                installmentsData={installmentsData}
                paymentDue={amountDue}
              />
            ),
          }}
        />
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
});

export default ActiveDetailsScreen;
